import {
  Flex,
  Text,
  Box,
  Badge,
  Code,
  Button,
  Collapse,
} from '@chakra-ui/react';
import React, { useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { FinleyAi } from '@finley-technologies/finley-types';

import { DownloadContainer } from './DownloadContainer';

// Type definitions for conversation message content
interface ToolUse {
  name: string;
  input: Record<string, unknown>;
}

interface ToolResult {
  toolUseId: string;
  content: Record<string, unknown>;
}

interface ConversationItem {
  text?: string;
  toolUse?: ToolUse;
}

interface ConversationContent extends Array<ConversationItem> {}

interface ConversationMessage {
  role: 'assistant' | 'user' | string;
  content: string; // JSON string that parses to ConversationContent or ToolResult array
}

const CollapsibleToolResult = ({
  toolResult,
  index,
}: {
  toolResult: ToolResult;
  index: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Box
      key={index}
      mb="16px"
      p="12px"
      borderRadius="md"
      borderLeft="4px solid"
      borderColor="green.400"
    >
      <Flex align="center" mb="8px" justify="space-between">
        <Flex align="center">
          <Badge colorScheme="green" mr="8px">
            Tool Result
          </Badge>
          <Text fontSize="xs" fontWeight="600" color="green.300">
            ✅ {toolResult.toolUseId}
          </Text>
        </Flex>
        <Button
          size="xs"
          variant="ghost"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Collapse' : 'Expand'}
        </Button>
      </Flex>
      <Collapse in={isExpanded}>
        <Box
          p="8px"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.600"
        >
          <Code fontSize="xs" display="block" whiteSpace="pre-wrap">
            {JSON.stringify(toolResult.content, null, 2)}
          </Code>
        </Box>
      </Collapse>
    </Box>
  );
};

export const VirtualSheetThread = ({
  thread,
}: {
  thread: FinleyAi.FinleyAiThreadWithPromptResponses;
}) => {
  finleyAssert(
    thread.type === 'virtualSheetConfig',
    `Invalid thread type: ${thread.type}, expected 'virtualSheetConfig'`,
  );

  const virtualSheetResponses = thread.promptResponses;

  const getStatusColorScheme = (
    status: FinleyAi.FinleyAiPromptResponseStatus,
  ) => {
    switch (status) {
      case 'success':
        return 'green';
      case 'in-progress':
        return 'yellow';
      case 'failure':
        return 'red';
      case 'initiated':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const renderConversationMessage = (
    message: ConversationMessage,
    index: number,
  ) => {
    try {
      const content:
        | ConversationContent
        | { toolResult: ToolResult }[] = JSON.parse(message.content);

      if (message.role === 'assistant') {
        const assistantContent = content as ConversationContent;
        return (
          <Box
            key={index}
            mb="16px"
            p="12px"
            borderRadius="md"
            borderLeft="4px solid"
            borderColor="blue.400"
          >
            <Flex align="center" mb="8px">
              <Badge colorScheme="blue" mr="8px">
                Assistant
              </Badge>
            </Flex>
            {assistantContent.map(
              (item: ConversationItem, itemIndex: number) => (
                // eslint-disable-next-line react/no-array-index-key
                <Box key={itemIndex} mb="8px">
                  {item.text && (
                    <Text fontSize="sm" whiteSpace="pre-line" mb="8px">
                      {item.text}
                    </Text>
                  )}
                  {item.toolUse && (
                    <Box
                      p="8px"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.600"
                    >
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        mb="4px"
                        color="purple.300"
                      >
                        🛠️ Tool Request: {item.toolUse.name}
                      </Text>
                      <Code fontSize="xs" display="block" whiteSpace="pre-wrap">
                        {JSON.stringify(item.toolUse.input, null, 2)}
                      </Code>
                    </Box>
                  )}
                </Box>
              ),
            )}
          </Box>
        );
      }

      if (message.role === 'user') {
        const userContent = content as { toolResult: ToolResult }[];
        if (userContent[0]?.toolResult) {
          return (
            <CollapsibleToolResult
              toolResult={userContent[0].toolResult}
              index={index}
            />
          );
        }
      }
    } catch (e) {
      // Fallback for malformed content
      return (
        <Box key={index} mb="16px" p="12px" borderRadius="md">
          <Badge colorScheme="gray" mr="8px">
            {message.role}
          </Badge>
          <Text fontSize="sm" color="gray.300">
            {message.content}
          </Text>
        </Box>
      );
    }

    return null;
  };

  const renderFinalAnswers = (
    promptResult: FinleyAi.VirtualSheetConfigPromptResult,
  ) => {
    if (!promptResult?.answers) return null;

    return (
      <Box
        mt="24px"
        p="16px"
        borderRadius="md"
        border="1px solid"
        borderColor="green.600"
      >
        <Text fontWeight="600" fontSize="md" mb="16px" color="green.300">
          📊 Generated Virtual Sheet Configuration
        </Text>
        {promptResult.answers?.map(
          (
            answer: FinleyAi.FinleyAiVirtualSheetFormulaAnswer,
            index: number,
          ) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              mb="16px"
              p="12px"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.600"
            >
              <Text
                fontWeight="600"
                mb="8px"
                color="green.300"
              >{`${answer.fieldName}`}</Text>
              <Box mb="8px">
                <Text fontSize="xs" fontWeight="600" color="gray.400" mb="4px">
                  Formula:
                </Text>
                <Code fontSize="sm" display="block" p="8px" borderRadius="md">
                  {answer.formula}
                </Code>
              </Box>
              {answer.description && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="600"
                    color="gray.400"
                    mb="4px"
                  >
                    Description:
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    {answer.description}
                  </Text>
                </Box>
              )}
            </Box>
          ),
        )}
      </Box>
    );
  };

  const renderStatusProgress = (
    response: FinleyAi.VirtualSheetConfigPromptResponse,
  ) => {
    if (response.status !== 'in-progress') return null;

    const statusMessage =
      response.statusMetadata.error || response.statusMetadata.message;
    if (!statusMessage) return null;

    // Split by newlines and get last 3 non-empty lines
    const lines = statusMessage
      .split('\n')
      .filter((line) => line.trim() !== '');
    const lastThreeLines = lines.slice(-3);

    if (lastThreeLines.length === 0) return null;

    return (
      <Box
        mt="12px"
        p="12px"
        borderRadius="md"
        borderLeft="4px solid"
        borderColor="yellow.400"
      >
        <Flex align="center" mb="8px">
          <Badge colorScheme="yellow" mr="8px">
            Processing
          </Badge>
        </Flex>
        <Text fontSize="sm" color="gray.300" whiteSpace="pre-line">
          {lastThreeLines.join('\n')}
        </Text>
      </Box>
    );
  };

  return (
    <Flex flexDir="column" width="100%">
      <Text fontWeight={600}>Virtual Sheet Config</Text>

      {virtualSheetResponses.map((response) => (
        <Box key={response.id} mt="16px">
          <Flex align="center" mb="16px">
            <Badge colorScheme={getStatusColorScheme(response.status)} mr="8px">
              {response.status}
            </Badge>
            <Text fontSize="sm" color="gray.400">
              {new Date(response.created_at).toLocaleString()}
            </Text>
          </Flex>

          {renderStatusProgress(response)}

          {response.engineeredPrompt?.conversation && (
            <Box>
              <Text fontWeight="600" mb="12px">
                Conversation Flow:
              </Text>
              {response.engineeredPrompt.conversation.map((message, index) =>
                renderConversationMessage(message, index),
              )}
            </Box>
          )}

          {response.promptResult && renderFinalAnswers(response.promptResult)}

          {/* Download link for successful responses */}
          {response.status === 'success' && response.promptResult && (
            <DownloadContainer finleyAiPromptResponseId={response.id} />
          )}
        </Box>
      ))}
    </Flex>
  );
};
